<template>
  <div class="card">
    <div class="card-body">
      <p class="text-muted small mb-4">
        Definiere die Felder, die für jedes Bereichsmitglied erforderlich sind.
        <br /><strong>Hinweis:</strong> Vorhandene Felder können nur gelöscht oder in der
        Reihenfolge geändert werden.
      </p>
      <MemberFieldsInfo class="mb-3" />

      <div
        v-for="(field, index) in form.fields"
        :key="field.id || `new-${index}`"
        class="card mb-2"
      >
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex align-items-center">
              <strong>Feld #{{ index + 1 }}</strong>
              <span v-if="field.id" class="badge bg-secondary ms-2 small"
                >Vorhandenes Feld</span
              >
              <span v-else class="badge bg-success ms-2 small">Neues Feld</span>
            </div>
            <div class="btn-group">
              <!-- Reihenfolge-Buttons -->
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="moveField(index, -1)"
                :disabled="index === 0"
                title="Nach oben"
              >
                <font-awesome-icon icon="arrow-up" />
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="moveField(index, 1)"
                :disabled="index === form.fields.length - 1"
                title="Nach unten"
              >
                <font-awesome-icon icon="arrow-down" />
              </button>
              <!-- Löschen-Button -->
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                @click="removeField(index)"
                title="Feld löschen"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>

          <div class="row g-2">
            <div class="col-md-4">
              <label :for="`fieldName${index}`" class="form-label">Feldname</label>
              <input
                type="text"
                class="form-control"
                :id="`fieldName${index}`"
                v-model="field.name"
                placeholder="z.B. Geburtsdatum"
                :disabled="!!field.id"
                :class="{ 'bg-light': !!field.id, 'is-invalid': errors[`field-${index}-name`] }"
              />
              <div class="invalid-feedback" v-if="errors[`field-${index}-name`]">
                {{ errors[`field-${index}-name`] }}
              </div>
            </div>
            <div class="col-md-4">
              <label :for="`fieldType${index}`" class="form-label">Feldtyp</label>
              <select
                class="form-select"
                :id="`fieldType${index}`"
                v-model="field.type"
                :disabled="!!field.id"
                :class="{ 'bg-light': !!field.id }"
              >
                <option value="text">Text</option>
                <option value="email">E-Mail</option>
                <option value="number">Zahl</option>
                <option value="date">Datum</option>
                <option value="textarea">Textbereich</option>
                <option value="checkbox">Checkbox</option>
                <option value="select">Auswahl</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Pflichtfeld</label>
              <div class="form-switch-container ms-3">
                <div class="form-check form-switch form-switch-lg">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="`fieldRequired${index}`"
                    v-model="field.required"
                    role="switch"
                  />
                  <label class="form-check-label ms-5" :for="`fieldRequired${index}`">
                    {{ field.required ? 'Ja' : 'Nein' }}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Beschreibung und Platzhalter -->
          <div class="row g-2 mt-2">
            <div class="col-md-6">
              <label :for="`fieldDescription${index}`" class="form-label">
                Beschreibung
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary ms-2" 
                  @click="addLinkToDescription(index)"
                  title="Link zur Beschreibung hinzufügen"
                  :disabled="!!field.id"
                >
                  <font-awesome-icon icon="link" />
                </button>
              </label>
              <textarea
                class="form-control"
                :id="`fieldDescription${index}`"
                v-model="field.description"
                rows="2"
                placeholder="Optionale Beschreibung für das Feld"
                :disabled="!!field.id"
                :class="{ 'bg-light': !!field.id }"
              ></textarea>
              <div class="form-text">
                <small>
                  <strong>Hinweis:</strong> Bei Checkbox-Feldern wird die Beschreibung als Label verwendet.
                  Links können mit dem Link-Button hinzugefügt werden.
                </small>
              </div>
            </div>
            <div class="col-md-6" v-if="field.type !== 'checkbox' && field.type !== 'select' && field.type !== 'radio'">
              <label :for="`fieldPlaceholder${index}`" class="form-label">Platzhalter</label>
              <input
                type="text"
                class="form-control"
                :id="`fieldPlaceholder${index}`"
                v-model="field.placeholder"
                placeholder="Platzhaltertext für das Eingabefeld"
                :disabled="!!field.id"
                :class="{ 'bg-light': !!field.id }"
              />
              <div class="form-text">
                <small>Wird nur bei Text-, E-Mail-, Zahl- und Datumsfeldern angezeigt</small>
              </div>
            </div>
          </div>

          <div v-if="field.type === 'select'" class="mt-2">
            <label :for="`fieldOptions${index}`" class="form-label">Optionen</label>
            <input
              type="text"
              class="form-control"
              :id="`fieldOptions${index}`"
              v-model="field.options"
              placeholder="Optionen durch Komma getrennt"
              :disabled="!!field.id"
              :class="{ 'bg-light': !!field.id }"
            />
            <div class="form-text">
              <span v-if="field.id"
                >Optionen von vorhandenen Feldern können nicht geändert werden.</span
              >
              <span v-else>Gib die Optionen durch Komma getrennt ein.</span>
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-outline-primary mt-2 mb-4" @click="addField">
        <font-awesome-icon icon="plus" /> Neues Feld hinzufügen
      </button>

      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-secondary me-2" @click="handleCancel">
          Abbrechen
        </button>
        <button type="button" class="btn btn-primary" @click="saveSchema" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Mitgliederfelder speichern
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, onBeforeUnmount } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { teamService } from '@/api/teams'
import { showSuccess, showConfirmDialogWithDeny } from '@/services/notification'
import Swal from 'sweetalert2'
import MemberFieldsInfo from './MemberFieldsInfo.vue'

const props = defineProps({
  team: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['back', 'success', 'error'])

const loading = ref(false)
const deletedFields = ref([])
const originalData = ref(null)
const hasUnsavedChanges = ref(false)

const form = reactive({
  fields: []
})

const errors = reactive({})

const initForm = () => {
  if (!props.team) return

  form.fields = props.team.fields ? [...props.team.fields] : []

  form.fields.forEach((field, index) => {
    errors[`field-${index}-name`] = ''
  })
  deletedFields.value = []
  
  
  originalData.value = {
    fields: props.team.fields ? JSON.parse(JSON.stringify(props.team.fields)) : []
  }
  
  hasUnsavedChanges.value = false
}

const addField = () => {
  const newIndex = form.fields.length
  form.fields.push({
    name: '',
    type: 'text',
    required: false,
    options: '',
    description: '',
    placeholder: '',
  })
  errors[`field-${newIndex}-name`] = ''
  hasUnsavedChanges.value = true
}

const addLinkToDescription = async (index) => {
  const field = form.fields[index]
  const result = await Swal.fire({
    title: 'Link hinzufügen',
    html: `
      <div class="mb-3">
        <label for="linkText" class="form-label">Link-Text:</label>
        <input type="text" id="linkText" class="form-control" placeholder="z.B. Datenschutzerklärung">
      </div>
      <div class="mb-3">
        <label for="linkUrl" class="form-label">URL:</label>
        <input type="url" id="linkUrl" class="form-control" placeholder="https://example.com">
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Hinzufügen',
    cancelButtonText: 'Abbrechen',
    preConfirm: () => {
      const linkText = document.getElementById('linkText').value.trim()
      const linkUrl = document.getElementById('linkUrl').value.trim()
      
      if (!linkText || !linkUrl) {
        Swal.showValidationMessage('Bitte fülle beide Felder aus')
        return false
      }
      
      if (!linkUrl.startsWith('https://')) {
        Swal.showValidationMessage('URL muss mit https:// beginnen')
        return false
      }
      
      return { linkText, linkUrl }
    }
  })
  
  if (result.isConfirmed) {
    const { linkText, linkUrl } = result.value
    
    const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
    
    if (field.description) {
      field.description += ' ' + linkHtml
    } else {
      field.description = linkHtml
    }
    
    hasUnsavedChanges.value = true
  }
}

const moveField = (index, direction) => {
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= form.fields.length) return

  const fields = [...form.fields]
  const field = fields[index]
  fields[index] = fields[newIndex]
  fields[newIndex] = field
  form.fields.splice(0, form.fields.length, ...fields)
  hasUnsavedChanges.value = true
}

const removeField = async (index) => {
  const fieldToRemove = form.fields[index]
  if (fieldToRemove.id) {
    const result = await Swal.fire({
      title: 'Feld löschen',
      text: `Möchtest du das Feld "${fieldToRemove.name}" wirklich löschen?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Löschen',
      cancelButtonText: 'Abbrechen',
    })

    if (!result.isConfirmed) {
      return
    }
    deletedFields.value.push(fieldToRemove.id)
  }
  form.fields.splice(index, 1)
  hasUnsavedChanges.value = true
}

const checkForChanges = () => {
  if (!originalData.value) return false
  
  if (deletedFields.value.length > 0) {
    return true
  }
  if (form.fields.length !== originalData.value.fields.length) {
    return true
  }
  for (let i = 0; i < form.fields.length; i++) {
    const currentField = form.fields[i]
    const originalField = originalData.value.fields[i]
    
    if (!originalField) {
      
      return true
    }
    
    if (currentField.name !== originalField.name ||
        currentField.type !== originalField.type ||
        currentField.required !== originalField.required ||
        currentField.options !== originalField.options ||
        currentField.description !== originalField.description ||
        currentField.placeholder !== originalField.placeholder) {
      return true
    }
  }
  
  return false
}

const confirmBeforeLeaving = async () => {
  if (!hasUnsavedChanges.value) {
    return true
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    
    await saveSchema()
    return true
  } else if (result.isDenied) {
    
    hasUnsavedChanges.value = false
    return true
  } else {
    
    return false
  }
}


const handleCancel = async () => {
  const canLeave = await confirmBeforeLeaving()
  if (canLeave) {
    emit('back')
  }
}

const saveSchema = async () => {
  if (!props.team) return

  
  form.fields.forEach((field, index) => {
    errors[`field-${index}-name`] = ''
  })

  let isValid = true

  
  form.fields.forEach((field, index) => {
    if (!field.name.trim()) {
      errors[`field-${index}-name`] = 'Feldname ist erforderlich'
      isValid = false
    } else {
      errors[`field-${index}-name`] = ''
    }
  })

  if (!isValid) return

  try {
    loading.value = true

    
    const fields = form.fields.map((field, index) => {
      if (field.id) {
        
        return {
          id: field.id,
          order: index,
          required: field.required,
          description: field.description,
          placeholder: field.placeholder,
        }
      } else {
        
        return {
          name: field.name,
          type: field.type,
          required: field.required,
          options: field.options,
          description: field.description,
          placeholder: field.placeholder,
          order: index,
        }
      }
    })

    const updatedTeam = await teamService.updateTeam(props.team.id, {
      fields: fields,
      deletedFields: deletedFields.value,
    })

    showSuccess('Mitgliederfelder wurde aktualisiert')
    hasUnsavedChanges.value = false
    emit('success', updatedTeam)
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Schemas:', err)
    emit('error', 'Mitgliederfelder konnte nicht aktualisiert werden')
  } finally {
    loading.value = false
  }
}


watch([() => form.fields, () => deletedFields.value], () => {
  hasUnsavedChanges.value = checkForChanges()
}, { deep: true })


const handleBeforeUnload = (e) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(() => {
  initForm()
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

// Router-Guard für Navigation
onBeforeRouteLeave(async (to, from, next) => {
  if (!hasUnsavedChanges.value) {
    next()
    return
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    try {
      await saveSchema()
      next()
    } catch (error) {
      console.error('Fehler beim Speichern:', error)
      next(false) // Verhindere Navigation bei Fehler
    }
  } else if (result.isDenied) {
    // Verwerfe die Änderungen und erlaube Navigation
    hasUnsavedChanges.value = false
    next()
  } else {
    // Benutzer hat abgebrochen - verhindere Navigation
    next(false)
  }
})

watch(() => props.team, () => {
  initForm()
}, { deep: true })
</script>
